import { get, post, put, patch, del } from '../../apiClient';
import { API_ENDPOINTS } from '../../config';

const customerService = {
  /**
   * Get customer by ID
   */
  getCustomerById: async (customerId) => {
    try {
      const response = await get(`${API_ENDPOINTS.CRM.CUSTOMERS}/${customerId}`);

      if (response && response.responseCode === 200) {
        return {
          success: true,
          customer: response.data
        };
      } else {
        return {
          success: false,
          error: response.responseMessage || 'Failed to retrieve customer',
          customer: null
        };
      }
    } catch (error) {
      
      return {
        success: false,
        error: error.message || 'Failed to retrieve customer',
        customer: null
      };
    }
  },

  /**
   * Get all customers with filters
   */
  getCustomers: async (filters = {}) => {
    try {
      const queryParams = new URLSearchParams();

      if (filters.search) queryParams.append('search', filters.search);
      if (filters.status) queryParams.append('status', filters.status);
      if (filters.page) queryParams.append('page', filters.page);
      if (filters.limit) queryParams.append('limit', filters.limit);
      if (filters.userId) queryParams.append('userId', filters.userId);

      const url = queryParams.toString()
        ? `${API_ENDPOINTS.CRM.CUSTOMERS}?${queryParams.toString()}`
        : API_ENDPOINTS.CRM.CUSTOMERS;

      const response = await get(url);

      if (response && response.responseCode === 200) {
        const customers = Array.isArray(response.data)
          ? response.data
          : (response.data?.customers || []);

        return {
          success: true,
          customers: customers,
          pagination: response.data?.pagination || {}
        };
      } else {
        return {
          success: false,
          error: response.responseMessage || 'Failed to retrieve customers',
          customers: []
        };
      }
    } catch (error) {
      
      return {
        success: false,
        error: error.message || 'Failed to retrieve customers',
        customers: []
      };
    }
  },

  /**
   * Get Customer 360 list with pagination (no KPIs)
   */
  getCustomer360List: async (filters = {}, signal) => {
    try {
      const queryParams = new URLSearchParams();

      if (filters.search) queryParams.append('search', filters.search);
      if (filters.page) queryParams.append('page', filters.page);
      if (filters.limit) queryParams.append('limit', filters.limit);
      if (filters.status) queryParams.append('status', filters.status);
      if (filters.assignee) queryParams.append('assignee', filters.assignee);
      if (filters.minInvoices !== undefined) queryParams.append('minInvoices', filters.minInvoices);
      if (filters.maxInvoices !== undefined) queryParams.append('maxInvoices', filters.maxInvoices);
      if (filters.minValue !== undefined) queryParams.append('minValue', filters.minValue);
      if (filters.maxValue !== undefined) queryParams.append('maxValue', filters.maxValue);
      if (filters.startDate) queryParams.append('startDate', filters.startDate);
      if (filters.endDate) queryParams.append('endDate', filters.endDate);
      if (filters.sortBy) queryParams.append('sortBy', filters.sortBy);
      if (filters.sortOrder) queryParams.append('sortOrder', filters.sortOrder);

      const url = queryParams.toString()
        ? `${API_ENDPOINTS.CRM.CUSTOMERS_360}?${queryParams.toString()}`
        : API_ENDPOINTS.CRM.CUSTOMERS_360;

      const response = await get(url, { signal });

      if (response && response.responseCode === 200) {
        const data = response.data || {};
        return {
          success: true,
          customers: data.data || [],
          pagination: data.pagination || { page: 1, limit: 10, total: 0 }
        };
      } else {
        return {
          success: false,
          error: response?.responseMessage || 'Failed to retrieve Customer 360 data',
          customers: [],
          pagination: { page: 1, limit: 10, total: 0 }
        };
      }
    } catch (error) {
      
      return {
        success: false,
        error: error.message || 'Failed to retrieve Customer 360 data',
        customers: [],
        pagination: { page: 1, limit: 10, total: 0 }
      };
    }
  },

  /**
   * Get global Customer 360 KPIs
   */
  getCustomer360Kpis: async (filters = {}, signal) => {
    try {
      const queryParams = new URLSearchParams();
      if (filters.search) queryParams.append('search', filters.search);
      if (filters.status) queryParams.append('status', filters.status);
      if (filters.assignee) queryParams.append('assignee', filters.assignee);
      if (filters.minInvoices !== undefined) queryParams.append('minInvoices', filters.minInvoices);
      if (filters.maxInvoices !== undefined) queryParams.append('maxInvoices', filters.maxInvoices);
      if (filters.minValue !== undefined) queryParams.append('minValue', filters.minValue);
      if (filters.maxValue !== undefined) queryParams.append('maxValue', filters.maxValue);
      if (filters.startDate) queryParams.append('startDate', filters.startDate);
      if (filters.endDate) queryParams.append('endDate', filters.endDate);

      const url = queryParams.toString()
        ? `${API_ENDPOINTS.CRM.CUSTOMERS_360}/kpis?${queryParams.toString()}`
        : `${API_ENDPOINTS.CRM.CUSTOMERS_360}/kpis`;

      const response = await get(url, { signal });

      if (response && response.responseCode === 200) {
        const data = response.data || {};
        return {
          success: true,
          kpis: {
            totalCustomers: data.totalCustomers || 0,
            totalInvoices: data.totalInvoices || 0,
            totalValue: data.totalValue || 0
          }
        };
      } else {
        return {
          success: false,
          error: response?.responseMessage || 'Failed to retrieve Customer 360 KPIs',
          kpis: {
            totalCustomers: 0,
            totalInvoices: 0,
            totalValue: 0
          }
        };
      }
    } catch (error) {
      
      return {
        success: false,
        error: error.message || 'Failed to retrieve Customer 360 KPIs',
        kpis: {
          totalCustomers: 0,
          totalOrders: 0,
          totalValue: 0
        }
      };
    }
  },

  /**
   * Get full Customer 360 detail for a single customer
   */
  getCustomer360: async (customerId) => {
    try {
      if (!customerId) {
        return {
          success: false,
          error: 'Customer ID is required',
          data: null
        };
      }

      const url = `${API_ENDPOINTS.CRM.CUSTOMERS_360}/${customerId}`;
      const response = await get(url);

      if (response && response.responseCode === 200) {
        return {
          success: true,
          data: response.data || null
        };
      } else {
        return {
          success: false,
          error: response?.responseMessage || 'Failed to retrieve Customer 360 detail',
          data: null
        };
      }
    } catch (error) {
      
      return {
        success: false,
        error: error.message || 'Failed to retrieve Customer 360 detail',
        data: null
      };
    }
  },

  /**
   * Export a single customer's full 360 detail as a backend-generated Excel
   * (.xlsx) workbook and trigger the browser download. The workbook is built
   * entirely on the server; the frontend only requests the file and saves it.
   */
  exportCustomer360Detail: async (customerId, fallbackName = 'Customer') => {
    try {
      if (!customerId) {
        return { success: false, error: 'Customer ID is required' };
      }

      const url = `${API_ENDPOINTS.CRM.CUSTOMERS_360}/${customerId}/export`;
      const blob = await get(url, { responseType: 'blob' });

      const sanitized = String(fallbackName || 'Customer')
        .replace(/[\\/:*?"<>|]/g, '')
        .replace(/\s+/g, '_')
        .replace(/_+/g, '_')
        .replace(/^_+|_+$/g, '')
        .slice(0, 80) || 'Customer';
      const filename = `Customer_${sanitized}.xlsx`;

      const objectUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(objectUrl);

      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to export customer details'
      };
    }
  },

  /**
   * Get customer-specific KPIs
   */
  getCustomerKpis: async (customerId) => {
    try {
      if (!customerId) {
        return {
          success: false,
          error: 'Customer ID is required',
          kpis: null
        };
      }

      const url = `${API_ENDPOINTS.CRM.CUSTOMERS_360}/${customerId}/kpis`;
      const response = await get(url);

      if (response && response.responseCode === 200) {
        return {
          success: true,
          kpis: response.data || null
        };
      } else {
        return {
          success: false,
          error: response?.responseMessage || 'Failed to retrieve Customer KPIs',
          kpis: null
        };
      }
    } catch (error) {
      
      return {
        success: false,
        error: error.message || 'Failed to retrieve Customer KPIs',
        kpis: null
      };
    }
  },

  /**
   * Get customer activities with filters and pagination
   */
  getCustomerInteractions: async (customerId, filters = {}) => {
    try {
      if (!customerId) {
        return {
          success: false,
          error: 'Customer ID is required',
          activities: [],
          pagination: {}
        };
      }

      const queryParams = new URLSearchParams();

      if (filters.type && filters.type !== 'all') queryParams.append('type', filters.type);
      if (filters.outcome) queryParams.append('outcome', filters.outcome);
      if (filters.priority) queryParams.append('priority', filters.priority);
      if (filters.startDate) queryParams.append('startDate', filters.startDate);
      if (filters.endDate) queryParams.append('endDate', filters.endDate);
      if (filters.search) queryParams.append('search', filters.search);
      if (filters.sortBy) queryParams.append('sortBy', filters.sortBy);
      if (filters.sortOrder) queryParams.append('sortOrder', filters.sortOrder);
      if (filters.page) queryParams.append('page', filters.page);
      if (filters.limit) queryParams.append('limit', filters.limit);

      const url = queryParams.toString()
        ? `${API_ENDPOINTS.CRM.CUSTOMERS}/${customerId}/activities?${queryParams.toString()}`
        : `${API_ENDPOINTS.CRM.CUSTOMERS}/${customerId}/activities`;

      const response = await get(url);

      if (response && response.responseCode === 200) {
        return {
          success: true,
          activities: response.data?.activities || [],
          pagination: response.data?.pagination || {}
        };
      } else {
        return {
          success: false,
          error: response?.responseMessage || 'Failed to retrieve activities',
          activities: [],
          pagination: {}
        };
      }
    } catch (error) {
      
      return {
        success: false,
        error: error.message || 'Failed to retrieve activities',
        activities: [],
        pagination: {}
      };
    }
  },

  /**
   * Create a customer activity
   */
  createCustomerActivity: async (customerId, activityData = {}) => {
    try {
      if (!customerId) {
        return {
          success: false,
          error: 'Customer ID is required',
          activity: null
        };
      }

      const response = await post(`${API_ENDPOINTS.CRM.CUSTOMERS}/${customerId}/activities`, activityData);

      if (response && response.responseCode === 201) {
        return {
          success: true,
          activity: response.data || null
        };
      }

      return {
        success: false,
        error: response?.responseMessage || 'Failed to create activity',
        activity: null
      };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to create activity',
        activity: null
      };
    }
  },

  /**
   * Get all customer activities
   */
  getAllCustomerActivities: async (filters = {}) => {
    try {
      const queryParams = new URLSearchParams();

      if (filters.type && filters.type !== 'all') queryParams.append('type', filters.type);
      if (filters.customerId) queryParams.append('customerId', filters.customerId);
      if (filters.performedBy) queryParams.append('performedBy', filters.performedBy);
      if (filters.callStatus) queryParams.append('callStatus', filters.callStatus);
      if (filters.outcome) queryParams.append('outcome', filters.outcome);
      if (filters.priority) queryParams.append('priority', filters.priority);
      if (filters.startDate) queryParams.append('startDate', filters.startDate);
      if (filters.endDate) queryParams.append('endDate', filters.endDate);
      if (filters.search) queryParams.append('search', filters.search);
      if (filters.sortBy) queryParams.append('sortBy', filters.sortBy);
      if (filters.sortOrder) queryParams.append('sortOrder', filters.sortOrder);
      if (filters.page) queryParams.append('page', filters.page);
      if (filters.limit) queryParams.append('limit', filters.limit);

      const url = queryParams.toString()
        ? `${API_ENDPOINTS.CRM.CUSTOMER_ACTIVITIES}?${queryParams.toString()}`
        : API_ENDPOINTS.CRM.CUSTOMER_ACTIVITIES;

      const response = await get(url);

      if (response && response.responseCode === 200) {
        return {
          success: true,
          activities: response.data?.activities || [],
          pagination: response.data?.pagination || {}
        };
      }

      return {
        success: false,
        error: response?.responseMessage || 'Failed to retrieve customer activities',
        activities: [],
        pagination: {}
      };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to retrieve customer activities',
        activities: [],
        pagination: {}
      };
    }
  },

  /**
   * Get customer invoices with pagination and sorting (list fields only)
   */
  getCustomerInvoices: async (customerId, filters = {}) => {
    try {
      if (!customerId) {
        return {
          success: false,
          error: 'Customer ID is required',
          invoices: [],
          pagination: {}
        };
      }

      const queryParams = new URLSearchParams();

      if (filters.page) queryParams.append('page', filters.page);
      if (filters.limit) queryParams.append('limit', filters.limit);
      if (filters.sortBy) queryParams.append('sortBy', filters.sortBy);
      if (filters.sortOrder) queryParams.append('sortOrder', filters.sortOrder);
      if (filters.invoiceNumber) queryParams.append('invoiceNumber', filters.invoiceNumber);
      if (filters.startDate) queryParams.append('startDate', filters.startDate);
      if (filters.endDate) queryParams.append('endDate', filters.endDate);

      const url = queryParams.toString()
        ? `${API_ENDPOINTS.CRM.CUSTOMERS}/${customerId}/invoices?${queryParams.toString()}`
        : `${API_ENDPOINTS.CRM.CUSTOMERS}/${customerId}/invoices`;

      const response = await get(url);

      if (response && response.responseCode === 200) {
        return {
          success: true,
          invoices: response.data?.data || [],
          pagination: response.data?.pagination || {}
        };
      } else {
        return {
          success: false,
          error: response?.responseMessage || 'Failed to retrieve customer invoices',
          invoices: [],
          pagination: {}
        };
      }
    } catch (error) {
      
      return {
        success: false,
        error: error.message || 'Failed to retrieve customer invoices',
        invoices: [],
        pagination: {}
      };
    }
  },

  /**
   * Previous purchases of a product for a customer (from sales invoices).
   */
  getCustomerProductPurchaseHistory: async (customerId, filters = {}) => {
    try {
      if (!customerId) {
        return {
          success: false,
          error: 'Customer ID is required',
          items: []
        };
      }

      const queryParams = new URLSearchParams();
      if (filters.productName) queryParams.append('productName', filters.productName);

      const url = `${API_ENDPOINTS.CRM.CUSTOMERS}/${customerId}/product-purchase-history?${queryParams.toString()}`;
      const response = await get(url);

      if (response && response.responseCode === 200) {
        return {
          success: true,
          items: response.data?.items || [],
          matchedBy: response.data?.matchedBy || null
        };
      }

      return {
        success: false,
        error: response?.responseMessage || 'Failed to retrieve product purchase history',
        items: []
      };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to retrieve product purchase history',
        items: []
      };
    }
  },

  /**
   * Get single invoice detail (line items + payments)
   */
  getCustomerInvoiceById: async (customerId, invoiceId) => {
    try {
      if (!customerId || !invoiceId) {
        return {
          success: false,
          error: 'Customer ID and invoice ID are required',
          invoice: null
        };
      }

      const response = await get(
        `${API_ENDPOINTS.CRM.CUSTOMERS}/${customerId}/invoices/${invoiceId}`
      );

      if (response && response.responseCode === 200) {
        return {
          success: true,
          invoice: response.data || null
        };
      }

      return {
        success: false,
        error: response?.responseMessage || 'Failed to retrieve invoice details',
        invoice: null
      };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to retrieve invoice details',
        invoice: null
      };
    }
  },

  /**
   * Get customer reviews by customer ID
   */
  getCustomerReviews: async (customerId, filters = {}) => {
    try {
      if (!customerId) {
        return {
          success: false,
          error: 'Customer ID is required',
          reviews: [],
          pagination: {}
        };
      }

      const queryParams = new URLSearchParams();
      if (filters.page) queryParams.append('page', filters.page);
      if (filters.limit) queryParams.append('limit', filters.limit);

      const url = queryParams.toString()
        ? `${API_ENDPOINTS.CRM.CUSTOMER_REVIEWS}/${customerId}?${queryParams.toString()}`
        : `${API_ENDPOINTS.CRM.CUSTOMER_REVIEWS}/${customerId}`;

      const response = await get(url);

      if (response && response.responseCode === 200) {
        return {
          success: true,
          reviews: response.data?.reviews || [],
          pagination: response.data?.pagination || response.pagination || {}
        };
      }

      return {
        success: false,
        error: response?.responseMessage || 'Failed to retrieve customer reviews',
        reviews: [],
        pagination: {}
      };
    } catch (error) {
      
      return {
        success: false,
        error: error.message || 'Failed to retrieve customer reviews',
        reviews: [],
        pagination: {}
      };
    }
  },

  /**
   * Create customer review
   */
  createCustomerReview: async (payload) => {
    try {
      const response = await post(API_ENDPOINTS.CRM.CUSTOMER_REVIEWS, payload);

      if (response && response.responseCode === 201) {
        return {
          success: true,
          review: response.data?.review || null
        };
      }

      return {
        success: false,
        error: response?.responseMessage || 'Failed to create customer review'
      };
    } catch (error) {
      
      return {
        success: false,
        error: error.message || 'Failed to create customer review'
      };
    }
  },

  /**
   * Update customer isActive status
   */
  updateCustomerStatus: async (customerId, isActive) => {
    try {
      if (!customerId) {
        return {
          success: false,
          error: 'Customer ID is required'
        };
      }

      const response = await patch(`${API_ENDPOINTS.CRM.CUSTOMERS}/${customerId}/status`, {
        isActive
      });

      if (response && response.responseCode === 200) {
        return {
          success: true,
          customer: response.data
        };
      } else {
        return {
          success: false,
          error: response?.responseMessage || 'Failed to update customer status'
        };
      }
    } catch (error) {
      
      return {
        success: false,
        error: error.message || 'Failed to update customer status'
      };
    }
  },

  /**
   * Create a new customer
   */
  createCustomer: async (customerData) => {
    try {
      const response = await post(API_ENDPOINTS.CRM.CUSTOMERS, customerData);

      if (response && response.responseCode === 201) {
        return {
          success: true,
          customer: response.data
        };
      } else {
        return {
          success: false,
          error: response?.responseMessage || 'Failed to create customer'
        };
      }
    } catch (error) {
      
      return {
        success: false,
        error: error.message || 'Failed to create customer'
      };
    }
  },

  /**
   * Update an existing customer
   */
  updateCustomer: async (customerId, customerData) => {
    try {
      if (!customerId) {
        return {
          success: false,
          error: 'Customer ID is required'
        };
      }

      const response = await put(`${API_ENDPOINTS.CRM.CUSTOMERS}/${customerId}`, customerData);

      if (response && response.responseCode === 200) {
        return {
          success: true,
          customer: response.data
        };
      } else {
        return {
          success: false,
          error: response?.responseMessage || 'Failed to update customer'
        };
      }
    } catch (error) {
      
      return {
        success: false,
        error: error.message || 'Failed to update customer'
      };
    }
  },

  /**
   * Export Customer 360 list to CSV
   */
  exportCustomer360List: async (filters = {}) => {
    try {
      const queryParams = new URLSearchParams();

      if (filters.search) queryParams.append('search', filters.search);
      if (filters.status) queryParams.append('status', filters.status);
      if (filters.minInvoices !== undefined) queryParams.append('minInvoices', filters.minInvoices);
      if (filters.maxInvoices !== undefined) queryParams.append('maxInvoices', filters.maxInvoices);
      if (filters.minValue !== undefined) queryParams.append('minValue', filters.minValue);
      if (filters.maxValue !== undefined) queryParams.append('maxValue', filters.maxValue);
      if (filters.startDate) queryParams.append('startDate', filters.startDate);
      if (filters.endDate) queryParams.append('endDate', filters.endDate);

      // Construct export URL
      const url = `${API_ENDPOINTS.CRM.CUSTOMERS_360}/export?${queryParams.toString()}`;

      // For file download, we don't use the standard fetch wrapper because we need the blob
      // We'll construct the full URL and return it for the component to handle, or handle it here
      return {
        success: true,
        downloadUrl: url
      };
    } catch (error) {
      
      return {
        success: false,
        error: error.message || 'Failed to generate export URL'
      };
    }
  },

  /**
   * Get industries configuration
   */
  getIndustries: async () => {
    try {
      const response = await get(API_ENDPOINTS.CRM.CONFIG.INDUSTRIES);

      // Handle the case where the API returns the data directly or wrapped in a success object
      if (response && (response.responseCode === 200 || response.success === true)) {
        return {
          success: true,
          data: response.data || []
        };
      } else if (Array.isArray(response)) {
        // Fallback if the API returns just the array
        return {
          success: true,
          data: response
        };
      } else {
        return {
          success: false,
          error: response?.responseMessage || response?.error || 'Failed to retrieve industries'
        };
      }
    } catch (error) {
      
      return {
        success: false,
        error: error.message || 'Failed to retrieve industries'
      };
    }
  },

  /**
   * Get decision makers configuration
   */
  getDecisionMakers: async () => {
    try {
      const response = await get(API_ENDPOINTS.CRM.CONFIG.DECISION_MAKERS);

      // Handle the case where the API returns the data directly or wrapped in a success object
      if (response && (response.responseCode === 200 || response.success === true)) {
        return {
          success: true,
          data: response.data || []
        };
      } else if (Array.isArray(response)) {
        // Fallback if the API returns just the array
        return {
          success: true,
          data: response
        };
      } else {
        return {
          success: false,
          error: response?.responseMessage || response?.error || 'Failed to retrieve decision makers'
        };
      }
    } catch (error) {
      
      return {
        success: false,
        error: error.message || 'Failed to retrieve decision makers'
      };
    }
  },
  /**
   * Preview customers for bulk upload
   */
  bulkPreview: async (customers) => {
    try {
      const response = await post(`${API_ENDPOINTS.CRM.CUSTOMERS}/bulk-preview`, { customers });
      if (response && response.responseCode === 200) {
        return {
          success: true,
          data: response.data
        };
      } else {
        return {
          success: false,
          error: response?.responseMessage || 'Failed to generate preview'
        };
      }
    } catch (error) {
      
      return {
        success: false,
        error: error.message || 'Failed to generate preview'
      };
    }
  },

  /**
   * Start an async bulk upload of customers. Returns a batchId for progress polling.
   */
  bulkUpload: async (customers, fileName = '') => {
    try {
      const response = await post(`${API_ENDPOINTS.CRM.CUSTOMERS}/bulk-upload`, { customers, fileName });
      if (response && (response.responseCode === 202 || response.responseCode === 201 || response.responseCode === 200)) {
        return {
          success: true,
          data: response.data
        };
      } else {
        return {
          success: false,
          error: response?.responseMessage || 'Failed to perform bulk upload'
        };
      }
    } catch (error) {
      
      return {
        success: false,
        error: error.message || 'Failed to perform bulk upload'
      };
    }
  },

  /**
   * Poll the progress/result of an async bulk upload.
   */
  getBulkUploadProgress: async (batchId) => {
    try {
      const response = await get(`${API_ENDPOINTS.CRM.CUSTOMERS}/bulk-upload/progress/${batchId}`);
      if (response && response.responseCode === 200) {
        return {
          success: true,
          data: response.data
        };
      }
      return {
        success: false,
        error: response?.responseMessage || 'Failed to retrieve import progress'
      };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to retrieve import progress'
      };
    }
  },

  /**
   * Block a customer
   */
  blockCustomer: async (customerId, reason) => {
    try {
      if (!customerId) {
        return { success: false, error: 'Customer ID is required' };
      }
      const response = await patch(`${API_ENDPOINTS.CRM.CUSTOMERS}/${customerId}/block`, { reason });
      if (response && response.responseCode === 200) {
        return { success: true, customer: response.data };
      } else {
        return { success: false, error: response?.responseMessage || 'Failed to block customer' };
      }
    } catch (error) {
      
      return { success: false, error: error.message || 'Failed to block customer' };
    }
  },

  /**
   * Unblock a customer
   */
  unblockCustomer: async (customerId, reason) => {
    try {
      if (!customerId) {
        return { success: false, error: 'Customer ID is required' };
      }
      const response = await patch(`${API_ENDPOINTS.CRM.CUSTOMERS}/${customerId}/unblock`, { reason });
      if (response && response.responseCode === 200) {
        return { success: true, customer: response.data };
      } else {
        return { success: false, error: response?.responseMessage || 'Failed to unblock customer' };
      }
    } catch (error) {
      
      return { success: false, error: error.message || 'Failed to unblock customer' };
    }
  },

  /**
   * Send WhatsApp message to customer
   */
  sendWhatsApp: async (data) => {
    try {
      const response = await post(`${API_ENDPOINTS.CRM.CUSTOMERS}/whatsapp/send`, data);

      return {
        success: response.success,
        data: response.data,
        message: response.responseMessage
      };
    } catch (error) {
      
      return {
        success: false,
        error: error.response?.data?.responseMessage || "Failed to send WhatsApp message"
      };
    }
  },

  /**
   * Get customer financial summary (outstanding / received)
   */
  getCustomerFinancials: async (customerId) => {
    try {
      if (!customerId) {
        return { success: false, error: 'Customer ID is required', financials: null };
      }

      const response = await get(`${API_ENDPOINTS.CRM.CUSTOMERS}/${customerId}/financials`);

      if (response && response.responseCode === 200) {
        return {
          success: true,
          financials: response.data || null
        };
      }

      return {
        success: false,
        error: response?.responseMessage || 'Failed to retrieve financial summary',
        financials: null
      };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to retrieve financial summary',
        financials: null
      };
    }
  },

  /**
   * Get customer payment history (paginated + filters)
   */
  getCustomerPayments: async (customerId, filters = {}) => {
    try {
      if (!customerId) {
        return {
          success: false,
          error: 'Customer ID is required',
          payments: [],
          pagination: {}
        };
      }

      const queryParams = new URLSearchParams();
      if (filters.page) queryParams.append('page', filters.page);
      if (filters.limit) queryParams.append('limit', filters.limit);
      if (filters.invoiceNumber) queryParams.append('invoiceNumber', filters.invoiceNumber);
      if (filters.paymentMethod) queryParams.append('paymentMethod', filters.paymentMethod);
      if (filters.paymentStatus) queryParams.append('paymentStatus', filters.paymentStatus);
      if (filters.startDate) queryParams.append('startDate', filters.startDate);
      if (filters.endDate) queryParams.append('endDate', filters.endDate);
      if (filters.search) queryParams.append('search', filters.search);

      const url = queryParams.toString()
        ? `${API_ENDPOINTS.CRM.CUSTOMERS}/${customerId}/payments?${queryParams.toString()}`
        : `${API_ENDPOINTS.CRM.CUSTOMERS}/${customerId}/payments`;

      const response = await get(url);

      if (response && response.responseCode === 200) {
        return {
          success: true,
          payments: response.data?.payments || [],
          pagination: response.data?.pagination || response.pagination || {}
        };
      }

      return {
        success: false,
        error: response?.responseMessage || 'Failed to retrieve payment history',
        payments: [],
        pagination: {}
      };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to retrieve payment history',
        payments: [],
        pagination: {}
      };
    }
  },

  /**
   * Get payment details by ID
   */
  getCustomerPaymentById: async (customerId, paymentId) => {
    try {
      if (!customerId || !paymentId) {
        return { success: false, error: 'Customer ID and Payment ID are required', payment: null };
      }

      const response = await get(
        `${API_ENDPOINTS.CRM.CUSTOMERS}/${customerId}/payments/${paymentId}`
      );

      if (response && response.responseCode === 200) {
        return { success: true, payment: response.data || null };
      }

      return {
        success: false,
        error: response?.responseMessage || 'Failed to retrieve payment',
        payment: null
      };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to retrieve payment',
        payment: null
      };
    }
  },

  /**
   * Search invoices for payment entry (includes outstanding balance)
   */
  searchPaymentInvoices: async (customerId, filters = {}) => {
    try {
      if (!customerId) {
        return {
          success: false,
          error: 'Customer ID is required',
          invoices: [],
          pagination: {}
        };
      }

      const queryParams = new URLSearchParams();
      if (filters.search) queryParams.append('search', filters.search);
      if (filters.page) queryParams.append('page', filters.page);
      if (filters.limit) queryParams.append('limit', filters.limit);
      if (filters.invoiceId) queryParams.append('invoiceId', filters.invoiceId);

      const url = queryParams.toString()
        ? `${API_ENDPOINTS.CRM.CUSTOMERS}/${customerId}/payment-invoices?${queryParams.toString()}`
        : `${API_ENDPOINTS.CRM.CUSTOMERS}/${customerId}/payment-invoices`;

      const response = await get(url);

      if (response && response.responseCode === 200) {
        return {
          success: true,
          invoices: response.data?.invoices || [],
          pagination: response.data?.pagination || response.pagination || {}
        };
      }

      return {
        success: false,
        error: response?.responseMessage || 'Failed to search invoices',
        invoices: [],
        pagination: {}
      };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to search invoices',
        invoices: [],
        pagination: {}
      };
    }
  },

  /**
   * Record a customer payment (append-only)
   */
  addCustomerPayment: async (customerId, paymentData = {}) => {
    try {
      if (!customerId) {
        return { success: false, error: 'Customer ID is required' };
      }

      const response = await post(
        `${API_ENDPOINTS.CRM.CUSTOMERS}/${customerId}/payments`,
        paymentData
      );

      if (response && (response.responseCode === 201 || response.responseCode === 200)) {
        return {
          success: true,
          data: response.data || null,
          message: response.responseMessage
        };
      }

      return {
        success: false,
        error: response?.responseMessage || response?.error || 'Failed to record payment'
      };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to record payment'
      };
    }
  },

  /**
   * Check for duplicate customer by email or phone
   */
  checkDuplicate: async (params = {}) => {
    try {
      const queryParams = new URLSearchParams();
      if (params.email) queryParams.append('email', params.email);
      if (params.phone) queryParams.append('phone', params.phone);

      const url = `${API_ENDPOINTS.CRM.CUSTOMERS}/check-duplicate?${queryParams.toString()}`;
      const response = await get(url);

      if (response && response.responseCode === 200) {
        return {
          success: true,
          isDuplicate: response.data?.isDuplicate || false,
          customer: response.data?.customer || null
        };
      }
      return { success: false, error: response?.responseMessage || 'Duplicate check failed' };
    } catch (error) {
      return { success: false, error: error.message || 'Duplicate check failed' };
    }
  },

  /**
   * Get the customer analytics dashboard (single request, scoped to one customer).
   * @param {string} customerId - Mongo _id or human customerId
   * @param {object} filters - { range, startDate, endDate }
   */
  getCustomerAnalyticsDashboard: async (customerId, filters = {}) => {
    try {
      if (!customerId) {
        return { success: false, error: 'Customer ID is required', data: null };
      }

      const queryParams = new URLSearchParams();
      if (filters.range) queryParams.append('range', filters.range);
      if (filters.startDate) queryParams.append('startDate', filters.startDate);
      if (filters.endDate) queryParams.append('endDate', filters.endDate);

      const qs = queryParams.toString();
      const url = `${API_ENDPOINTS.CRM.CUSTOMERS}/${customerId}/analytics/dashboard${qs ? `?${qs}` : ''}`;
      const response = await get(url);

      if (response && response.responseCode === 200) {
        return { success: true, data: response.data || null };
      }

      return {
        success: false,
        error: response?.responseMessage || 'Failed to retrieve analytics',
        data: null
      };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to retrieve analytics',
        data: null
      };
    }
  },

  /**
   * Get paginated customer analytics timeline.
   * @param {string} customerId - Mongo _id or human customerId
   * @param {object} filters - { range, startDate, endDate, category, page, limit }
   */
  getCustomerAnalyticsTimeline: async (customerId, filters = {}) => {
    try {
      if (!customerId) {
        return { success: false, error: 'Customer ID is required', data: null };
      }

      const queryParams = new URLSearchParams();
      if (filters.range) queryParams.append('range', filters.range);
      if (filters.startDate) queryParams.append('startDate', filters.startDate);
      if (filters.endDate) queryParams.append('endDate', filters.endDate);
      if (filters.category) queryParams.append('category', filters.category);
      if (filters.page) queryParams.append('page', String(filters.page));
      if (filters.limit) queryParams.append('limit', String(filters.limit));

      const qs = queryParams.toString();
      const url = `${API_ENDPOINTS.CRM.CUSTOMERS}/${customerId}/analytics/timeline${qs ? `?${qs}` : ''}`;
      const response = await get(url);

      if (response && response.responseCode === 200) {
        return { success: true, data: response.data || null };
      }

      return {
        success: false,
        error: response?.responseMessage || 'Failed to retrieve timeline',
        data: null
      };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to retrieve timeline',
        data: null
      };
    }
  }
};

export default customerService;
